import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { environment } from "@env";
import {
  RequestServiceDTO,
  RequestServiceEventsHistoryDTO
} from "@shared/models/request-service-dto";
import {
  ServiceRequestFormDTO,
  ServiceRequestPreventiveCreationDTO
} from "@shared/models/service-request-form-dto";
import { StatusDTO } from "@shared/models/status-dto";
import { SearchFormDTO } from "../../modules/private/modules/service-request/components/models/search-form-dto";
import { map } from "rxjs/operators";
import { LanguageService } from "./language.service";
import { ReasonDTO } from "@shared/models/reason-dto";
import { ServiceRequestRatingDTO } from "@shared/models/service-request-rating-dto";
import {
  ServiceRequestCommentsDTO,
  ServiceRequestDetailsDTO,
  ServiceRequestSla,
  ServiceRequestTag,
  TagType
} from "@shared/models/service-request-details-dto";
import { PageManager } from "@shared/models/page-manager";
import { Pagination } from "@shared/models/pagination";
import { PeriodDTO } from "@shared/models/period-dto";
import { ServiceTypeDTO } from "@shared/models/service-type-dto";
import { BudgetDTO } from "@shared/models/budget-dto";
import { StepDTO } from "@shared/models/step-dto";
import { ModificationDTO } from "@shared/models/modification-dto";
import { DateHelperService } from "./date.helper.service";
import { ServiceRequestSimplifiedDTO } from "@shared/models/service-request-simplified-dto";
import { BillingInfoDTO } from "@shared/models/billing-info-dto";
import { TotalPendingServiceRequestDTO } from "@shared/models/total-pending-service-request";
import { dateToYearMonthDayFormat } from "src/app/modules/private/modules/car-workshop-calendar/services/car-workshop-calendar.service";
import { ApprovalQueueDTO } from "@core/dtos/approval-queue";
import { ServiceRequestCreationPostDTO } from "@core/dtos";
import { TagsFilterDTO } from "@core/models";

@Injectable({
  providedIn: "root"
})
export class ServiceRequestService {
  private serviceRequestUrl = "service-requests/";
  private schedulingRequestUrl = "scheduling-requests/";
  private serviceRequestUrlV2 = "v2/service-requests/";

  route: any;

  constructor(
    private http: HttpClient,
    private languageService: LanguageService,
    private dateHelperService: DateHelperService
  ) {}

  findById(id: number): Observable<ServiceRequestDetailsDTO> {
    return this.http.get<ServiceRequestDetailsDTO>(
      environment.apiUrl + `${this.serviceRequestUrl}${id}?web=true`
    );
  }

  edit(serviceRequestFormDTO: ServiceRequestFormDTO): Observable<any> {
    return this.http.put<any>(
      environment.apiUrl + this.serviceRequestUrl + serviceRequestFormDTO.id,
      serviceRequestFormDTO
    );
  }

  editWorkshopPrevisionDate(
    id: number,
    payload: { previsionDate: string; period: string }
  ): Observable<any> {
    return this.http.patch<any>(
      `${environment.apiUrl}${this.serviceRequestUrl}${id}/edit-workshop-prevision-date`,
      payload
    );
  }

  unavailable(carWorkshopId: number, isPuc: boolean) {
    if (!carWorkshopId) {
      return of([]);
    }

    return this.http.get<any>(
      `${
        environment.apiUrl
      }car-workshops/agenda/unavailable-periods/${carWorkshopId}?isPuc=${
        isPuc || false
      }&initialDate=${dateToYearMonthDayFormat(new Date())}`
    );
  }

  saveRescheduling(id: number, payload: any): Observable<any> {
    return this.http.put<any>(
      `${environment.apiUrl}${this.serviceRequestUrl}${id}/rescheduling`,
      payload
    );
  }

  saveRating(
    serviceRequestId: number,
    serviceRequestRatingDTO: ServiceRequestRatingDTO
  ): Observable<ServiceRequestRatingDTO> {
    return this.http.post<ServiceRequestRatingDTO>(
      `${environment.apiUrl}${this.serviceRequestUrl}${serviceRequestId}/ratings`,
      serviceRequestRatingDTO
    );
  }

  listStatus(): Observable<StatusDTO[]> {
    return this.http.get<StatusDTO[]>(
      environment.apiUrl + `${this.serviceRequestUrl}` + `service-status`
    );
  }

  listCancellationReason(type: string): Observable<ReasonDTO[]> {
    const query = `?type=${type}`;
    return this.http.get<ReasonDTO[]>(
      environment.apiUrl +
        `${this.serviceRequestUrl}reasons-cancellation${query}`
    );
  }

  notifyDriverServiceFinished(serviceRequestId: number): Observable<any> {
    return this.http.post<any>(
      `${environment.apiUrl}${this.schedulingRequestUrl}vehicle-ready/${serviceRequestId}`,
      serviceRequestId
    );
  }

  findByFilters(
    searchFormDTO: SearchFormDTO,
    pageManager: PageManager,
    sort: string
  ): Observable<Pagination<RequestServiceDTO[]>> {
    const body: any = {};
    if (searchFormDTO.vehicleId) {
      body.vehicleId = searchFormDTO.vehicleId;
    }
    if (searchFormDTO.serviceType) {
      body.serviceType = searchFormDTO.serviceType;
    }
    if (searchFormDTO.confirmationDate) {
      body.confirmationDate = this.dateHelperService.convertToDefaultFormat(
        searchFormDTO.confirmationDate
      );
    }
    if (searchFormDTO.blockCode) {
      body.blockCode = searchFormDTO.blockCode;
    }
    if (searchFormDTO.carWorkshopId) {
      body.carWorkshopId = searchFormDTO.carWorkshopId;
    }
    if (searchFormDTO.carWorkshopName) {
      body.carWorkshopName = searchFormDTO.carWorkshopName;
    }
    if (searchFormDTO.countryId) {
      body.countryId = searchFormDTO.countryId;
    }
    if (searchFormDTO.stateId) {
      body.stateId = searchFormDTO.stateId;
    }
    if (searchFormDTO.cityId) {
      body.cityId = searchFormDTO.cityId;
    }
    if (searchFormDTO.clientId) {
      body.clientId = searchFormDTO.clientId;
    }
    if (searchFormDTO.clientCorporateName) {
      body.clientCorporateName = searchFormDTO.clientCorporateName;
    }
    if (searchFormDTO.listStatus && searchFormDTO.listStatus.length) {
      body.listStatus = searchFormDTO.listStatus;
    }
    if (searchFormDTO.requestId) {
      body.requestId = searchFormDTO.requestId;
    }
    if (searchFormDTO.backofficePendency) {
      body.backofficePendency = searchFormDTO.backofficePendency;
    }
    if (searchFormDTO.carWorkshopPendency) {
      body.carWorkshopPendency = searchFormDTO.carWorkshopPendency;
    }
    if (searchFormDTO.vehicleLicensePlate) {
      body.licensePlate = searchFormDTO.vehicleLicensePlate;
    }
    if (searchFormDTO.tags && Array.isArray(searchFormDTO.tags)) {
      body.tags = searchFormDTO.tags;
    }
    if (searchFormDTO.filterCondition)
      body.filterCondition = searchFormDTO.filterCondition;

    if (searchFormDTO.isComplementary) {
      body.isComplementary = searchFormDTO.isComplementary;
    }
    if (searchFormDTO.isClientQuestioningPending) {
      body.isClientQuestioningPending =
        searchFormDTO.isClientQuestioningPending;
    }
    if (searchFormDTO.stepStatus) {
      body.stepStatus = searchFormDTO.stepStatus;
    }
    if (searchFormDTO.blocks) {
      body.blocks = searchFormDTO.blocks;
    }
    if (searchFormDTO.categoryId) {
      body.categoryId = searchFormDTO.categoryId;
    }
    if (searchFormDTO.budgetAnalystId) {
      body.budgetAnalystId = searchFormDTO.budgetAnalystId;
    }
    if (searchFormDTO.workshopRefused) {
      body.workshopRefused = searchFormDTO.workshopRefused;
    }
    if (searchFormDTO.haveGuaranteeOrCourtesy) {
      body.haveGuaranteeOrCourtesy = searchFormDTO.haveGuaranteeOrCourtesy;
    }
    if (searchFormDTO.filterByHeadquarter) {
      body.filterByHeadquarter = searchFormDTO.filterByHeadquarter;
    }
    if (searchFormDTO.filterBySubsidiary) {
      body.filterBySubsidiary = searchFormDTO.filterBySubsidiary;
    }
    if (searchFormDTO.attendanceCategory === true) {
      body.attendanceCategory = true;
    }
    if (searchFormDTO.finishedCategory === true) {
      body.finishedCategory = true;
    }

    return this.http.post<Pagination<RequestServiceDTO[]>>(
      `${environment.apiUrl}${this.serviceRequestUrl}filters?${sort}`,
      body,
      {
        params: {
          page: `${pageManager.pageIndex}`,
          size: `${pageManager.pageSize}`
        }
      }
    );
  }

  eventHistory = (serviceRequestId: number) => {
    return this.languageService.getLanguage$().pipe(
      map((currentLanguage) => {
        const headers = new HttpHeaders({
          "Content-Type": "application/json",
          "Accept-Language": currentLanguage
        });
        return this.http.get<RequestServiceEventsHistoryDTO[]>(
          `${environment.apiUrl}service-requests/${serviceRequestId}/history`,
          { headers }
        );
      })
    );
  };

  confirmVehicleWithdrawal(modificationDTO: ModificationDTO, observation) {
    return this.http.put<any>(
      environment.apiUrl +
        this.serviceRequestUrl +
        modificationDTO.id +
        "/remove-vehicle?observationRemoveVehicle=" +
        observation,
      modificationDTO
    );
  }

  confirmStartService(serviceId: number) {
    return this.http.put<any>(
      environment.apiUrl +
        this.serviceRequestUrl +
        serviceId +
        "/start-service",
      null
    );
  }

  finalizeService(serviceId: number) {
    return this.http.put<any>(
      environment.apiUrl +
        this.serviceRequestUrl +
        serviceId +
        "/finalize-service",
      null
    );
  }

  driverConfirmFinalizeService(
    serviceId: number,
    reason: string,
    observation: string
  ) {
    const query = `?reason=${reason}&observation=${observation}`;
    return this.http.put<ServiceRequestFormDTO>(
      environment.apiUrl +
        `${this.serviceRequestUrl}${serviceId}/service-confirmed-drive${query}`,
      null
    );
  }

  cancelRequest(
    serviceId: number,
    reason: string,
    observation: string
  ): Observable<ServiceRequestFormDTO> {
    const query = `?reason=${reason}&observation=${observation}`;
    return this.http.put<ServiceRequestFormDTO>(
      environment.apiUrl +
        `${this.serviceRequestUrl}${serviceId}/cancel${query}`,
      null
    );
  }

  disapproveRequest(
    serviceId: number,
    data: any
  ): Observable<ServiceRequestFormDTO> {
    const reasons = Array.isArray(data?.reason)
      ? data.reason
      : data?.reason
      ? [data.reason]
      : [];

    const requestData: any = { reasons };
    if (data.otherReason) {
      requestData.otherReason = data.otherReason;
    }

    return this.http.put<ServiceRequestFormDTO>(
      environment.apiUrl + `${this.serviceRequestUrl}${serviceId}/disapprove`,
      requestData
    );
  }

  notAppearOnSchedule(serviceId: number) {
    return this.http.put<any>(
      environment.apiUrl +
        this.serviceRequestUrl +
        serviceId +
        "/not-appear-schedule",
      null
    );
  }

  save(
    serviceRequestFormDTO: ServiceRequestFormDTO,
    isCarWorkshopLogged: boolean
  ): Observable<any> {
    const queryResource = isCarWorkshopLogged == true ? "carworkshop" : "";
    return this.http.post<any>(
      `${environment.apiUrl}${this.schedulingRequestUrl}${queryResource}`,
      serviceRequestFormDTO
    );
  }

  saveDocumentation(
    serviceRequestFormDTO: ServiceRequestFormDTO,
    isCarWorkshopLogged: boolean
  ): Observable<any> {
    const queryResource = isCarWorkshopLogged == true ? "carworkshop" : "";
    return this.http.post<any>(
      `${environment.apiUrl}service-request-document/${queryResource}`,
      serviceRequestFormDTO
    );
  }

  savePreventive(
    formDTO: ServiceRequestPreventiveCreationDTO
  ): Observable<ServiceRequestCreationPostDTO> {
    return this.http.post<ServiceRequestCreationPostDTO>(
      `${environment.apiV2Url}${this.serviceRequestUrl}`,
      formDTO
    );
  }

  createComplementaryBudget(
    serviceRequestId: number,
    data: any
  ): Observable<any> {
    data.date = this.dateHelperService.convertToDefaultFormat(data.date);
    return this.http.post<any>(
      `${environment.apiUrl}${this.schedulingRequestUrl}${serviceRequestId}/complementary`,
      null,
      { params: data }
    );
  }

  listAllTypePeriod(): Observable<PeriodDTO[]> {
    return this.http.get<PeriodDTO[]>(
      environment.apiUrl + `${this.schedulingRequestUrl}` + `period-types`
    );
  }

  listAllServiceType(): Observable<ServiceTypeDTO[]> {
    return this.http.get<PeriodDTO[]>(
      environment.apiUrl + `${this.schedulingRequestUrl}` + `service-types`
    );
  }

  listAllBudgetStatus() {
    return this.http.get<string[]>(
      environment.apiUrl + `${this.schedulingRequestUrl}` + `budget-status`
    );
  }

  delete(id: number): Observable<any> {
    return this.http.delete<any>(
      environment.apiUrl + `${this.schedulingRequestUrl}${id}`
    );
  }

  listBudgetsById(id: number): Observable<BudgetDTO[]> {
    return this.http.get<BudgetDTO[]>(
      environment.apiUrl + this.schedulingRequestUrl + `${id}/budgets`
    );
  }

  listStepsRequest(id: number): Observable<StepDTO[]> {
    return this.http.get<StepDTO[]>(
      environment.apiUrl + `${this.schedulingRequestUrl}${id}/service-status/`
    );
  }

  updateStep(stepDTO: StepDTO): Observable<any> {
    return this.http.put<any>(
      environment.apiUrl +
        this.schedulingRequestUrl +
        stepDTO.id +
        "/service-status/",
      stepDTO
    );
  }

  getStatusCancelStep(id: number): Observable<StepDTO[]> {
    return this.http.get<StepDTO[]>(
      `${environment.apiUrl}${this.schedulingRequestUrl}${id}/service-status-cancel`
    );
  }

  updateStatusCancelStep(stepDTO: StepDTO): Observable<any> {
    return this.http.put<any>(
      `${environment.apiUrl}${this.schedulingRequestUrl}${stepDTO.id}/service-status-cancel`,
      stepDTO
    );
  }

  updateOrderId(id: number, orderId: any): Observable<any> {
    return this.http.put<any>(
      `${environment.apiUrl}${this.schedulingRequestUrl}${id}/service-request-order-id`,
      orderId
    );
  }

  /**
   * @deprecated - Use RedirectService
   */
  getRouteUrlDefault(): string {
    return "service-request/";
  }

  /**
   * @deprecated - Use RedirectService
   */
  getRouteUrlDetails(idServiceRequest: number): string {
    return this.getRouteUrlDefault() + idServiceRequest + "/details/";
  }

  getSimplifiedsByIds(ids: number[]) {
    return this.http.get<ServiceRequestSimplifiedDTO[]>(
      `${environment.apiUrl}${
        this.serviceRequestUrl
      }simplified?ids=${ids.toString()}`
    );
  }

  getSla(id: number) {
    return this.http.get<ServiceRequestSla>(
      `${environment.apiUrl}${this.serviceRequestUrl}${id}/sla`
    );
  }

  getTags(id: number, filters?: TagsFilterDTO) {
    let params = new HttpParams();
    if (filters?.fromSystem) {
      params = params.append("fromSystem", "true");
    }
    return this.http.get<ServiceRequestTag[]>(
      `${environment.apiUrl}${this.serviceRequestUrl}${id}/tags`,
      {
        params
      }
    );
  }

  setTags(id: number, tags: { name: string; tagType: TagType }[]) {
    return this.http.put<ServiceRequestTag[]>(
      `${environment.apiUrl}${this.serviceRequestUrl}${id}/tags`,
      tags
    );
  }

  getBillingInfo(id: number) {
    return this.http.get<BillingInfoDTO>(
      `${environment.apiUrl}${this.serviceRequestUrl}${id}/billing-info`
    );
  }

  getTotalPendingServiceRequest() {
    return this.http.get<TotalPendingServiceRequestDTO>(
      `${environment.apiUrl}${this.serviceRequestUrl}pending-service-requests`
    );
  }

  complementaryIntention(id: number): Observable<ServiceRequestDetailsDTO> {
    return this.http.patch<ServiceRequestDetailsDTO>(
      `${environment.apiUrl}${this.serviceRequestUrl}${id}/complementary-intention`,
      null
    );
  }

  getApprovalQueue(
    pageManager: PageManager
  ): Observable<Pagination<ApprovalQueueDTO[]>> {
    let params = new HttpParams();
    params = params.append("page", pageManager.pageIndex.toString());
    params = params.append("size", pageManager.pageSize.toString());

    return this.http.get<Pagination<ApprovalQueueDTO[]>>(
      `${environment.apiUrl}${this.serviceRequestUrl}client-approval-queue`,
      { params }
    );
  }

  saveComment(serviceRequestId: number, comment: ServiceRequestCommentsDTO) {
    return this.http.post(
      `${environment.apiUrl}${this.serviceRequestUrl}${serviceRequestId}/service-request-comment`,
      { ...comment }
    );
  }

  savePrevisionDateAndPeriod(
    serviceId: number,
    prevision: { date: Date; period: string }
  ): Observable<any> {
    const { date, period } = prevision;

    return this.http.put(
      `${environment.apiUrl}${this.serviceRequestUrl}${serviceId}/prevision-update`,
      { date, period }
    );
  }

  indicateAttendance(serviceId: number): Observable<any> {
    return this.http.patch(
      `${environment.apiUrl}${this.serviceRequestUrl}${serviceId}/indicate-attendance`,
      null
    );
  }

  saveBudgetAnalyst(serviceRequestId: number, budgetAnalystId: number) {
    return this.http.patch(
      `${environment.apiUrl}${this.serviceRequestUrl}${serviceRequestId}/budget-analyst?budgetAnalystId=${budgetAnalystId}`,
      null
    );
  }

  removeBudgetAnalyst(serviceRequestId: number) {
    return this.http.patch(
      `${environment.apiUrl}${this.serviceRequestUrl}${serviceRequestId}/remove-budget-analyst`,
      null
    );
  }

  registerOutsideMaintenance(maintenance: Partial<ServiceRequestFormDTO>) {
    return this.http.post(
      `${environment.apiUrl}${this.schedulingRequestUrl}outside-request`,
      maintenance
    );
  }

  finishServiceSimplified(serviceRequestId: number) {
    return this.http.put(
      `${environment.apiUrl}${this.serviceRequestUrlV2}${serviceRequestId}/finish-service-simplified`,
      {}
    );
  }
}
